import type {QueryClient} from 'react-query';
import {QueryClientProvider} from 'react-query';
import React from 'react';
import {css} from '@sail/ui';
import type {RequestIframe} from '@sail/ui';

import {Provider as SailClassicThemeProvider} from '@sail/theme-classic';
import type {Intent} from '@sail/engine';
import type {ISecureContextMetadata} from '@stripe-internal/connect-embedded-lib';
import {
  BorderBoxContainer,
  MIN_WIDTH,
  InternalDomainContext,
} from '@stripe-internal/connect-embedded-lib';
import {useFlag} from '@sail/access';
import {useIntl} from '@sail/intl';
import {
  ObservabilityProvider,
  useObservabilityConfig,
} from '@sail/observability';

import type {FetchOverrideFn} from '../data-layer-client/buildFetchOverride';
import {ConnectElementErrorBoundary} from './ConnectElementErrorBoundary';
import {AuthProvider} from '../data-layer-client/AuthContext/AuthContext';
import {FeaturesProvider} from '../data-layer-client/FeaturesContext';
import type {SubmerchantAuthentication} from '../data-layer-client/SubmerchantAuthenticationContext';
import {SubmerchantAuthenticationContextProvider} from '../data-layer-client/SubmerchantAuthenticationContext';
import type {IDeferredPromise} from '../utils/DeferredPromise';
import type {AccountSessionClaim} from '../data-layer-frame/types';
import type {InheritedStyles} from './theming';
import type {ConnectElementImportKeys} from './ConnectJSInterface/ConnectElementList';
import {CONNECT_ELEMENT_IMPORTS} from './ConnectJSInterface/ConnectElementList';
import type {ConnectElementAttributes} from './ConnectElementAttributes';
import {AttributeProvider} from '../data-layer-client/AttributeContext/AttributeContext';
import {ConnectElementEnabledCheck} from './ConnectElementEnabledCheck';
import {ConnectEmbeddedLibProvider} from './ConnectEmbeddedLibProvider';
import type {ConnectElementEventEmitter} from './ConnectElementEventEmitter';
import {EventEmitterContext} from '../data-layer-client/EventEmitterContext';
import {RenderedStateProvider} from '../data-layer-client/RenderedStateContext/RenderedStateContext';
import type {ConnectElementRenderedState} from './ConnectElementRenderedState';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';
import {AccountOnboardingContainer} from '../components/Account/AccountOnboarding/AccountOnboardingContainer';
import {OnboardingProvider} from '../data-layer-client/OnboardingContext/OnboardingContext';
import type {IOnboardingState} from '../data-layer-client/OnboardingContext/OnboardingState';
import {ThemeProvider} from '../utils/theming/ThemeContext/ThemeContext';
import {ComponentLoadMetricContextProvider} from '../data-layer-client/ComponentLoadMetric';
import {AutoAnalyticsProvider} from '../utils/analytics/AutoAnalyticsProvider';
import KeyProviderWrapper from '../components/SharedComponents/Authentication/KeyProvider';
import {ConnectJsOptionsProvider} from '../data-layer-client/ConnectJsOptionsContext/ConnectJsOptionsContext';
import type {ConnectJsOptions} from './ConnectJsOptions';
import type {
  MetaOptions,
  UpdateOptions,
} from './ConnectJSInterface/InitAndUpdateOptionsTypes';
import {defaultHostApp} from './ConnectJSInterface/InitAndUpdateOptionsTypes';
import ConnectSdkProviderAccess from './ConnectSdkProviderAccess';
import ConnectSdkProviderObservability from './ConnectSdkProviderObservability';
import type {ConnectElementSupplementalFunctions} from './ConnectElementSupplementalFunctions';
import {SupplementalFunctionProvider} from '../data-layer-client/SupplementalFunctionContext/SupplementalFunctionContext';
import {ExternalFlagOverrideWrapper} from '../data-layer-client/ExternalFlagOverrideWrapper';
import {SupplementalObjectProvider} from '../data-layer-client/SupplementalObjectContext/SupplementalObjectContext';
import type {ConnectElementSupplementalObjects} from './ConnectElementSupplementalObjects';
import type {IPlatformFrameContext} from '../data-layer-client/PlatformFrameContext/PlatformFrameContext';
import {PlatformFrameContext} from '../data-layer-client/PlatformFrameContext/PlatformFrameContext';
import {SubmerchantInvalidSessionCheck} from '../data-layer-client/SubmerchantInvalidSessionCheck';
import {ConnectElementErrorBoundaryUi} from './ConnectElementErrorBoundaryUi';
import {LoggedOutProvider} from '../data-layer-client/LogoutContext/LoggedOutContext';
import type {ILoggedOutState} from './LoggedOutState';
import {PlatformViewportProvider} from '../ui-layer-frame/PlatformViewportContext/PlatformViewportContext';
import type {IPlatformViewportState} from '../ui-layer-frame/PlatformViewportContext/PlatformViewportState';
import MobileUIOverrides from '../ui-layer-frame/utils/MobileUIOverrides';
import {SecureContextEnforcer} from '../data-layer-client/SecureContextEnforcer/SecureContextEnforcer';
import {MobileSdkPreviewGateEnforcer} from '../data-layer-client/MobileSdkPreviewGateEnforcer/MobileSdkPreviewGateEnforcer';

export type Props = {
  initialRenderTime: number;
  fetchOverride: FetchOverrideFn;
  queryClient: QueryClient;
  deferredAuthPromise: IDeferredPromise<AccountSessionClaim>;
  submerchantAuthOverride?: SubmerchantAuthentication;
  authInfoPropertiesOverride?: AccountSessionClaim;
  publishableKey: string;
  connectElement: ConnectElementImportKeys;
  connectElementAttributes: ConnectElementAttributes;
  connectElementSupplementalFunctions: ConnectElementSupplementalFunctions;
  connectElementSupplementalObjects: ConnectElementSupplementalObjects;
  skipConnectElementEnabledCheck?: boolean;
  eventEmitter: ConnectElementEventEmitter;
  renderedState: ConnectElementRenderedState;
  frameMessenger: IFrameMessenger;
  onboardingState: IOnboardingState;
  loggedOutState: ILoggedOutState;
  platformViewportState: IPlatformViewportState;
  metaOptions: MetaOptions | undefined;
  connectJsOptions: ConnectJsOptions;
  updateConnectJSOptions: (options: UpdateOptions) => void;
  platformFrameFunctions?: IPlatformFrameContext;
  isInternalDomain: boolean;
  requestIframe?: RequestIframe;
  connectJsLoadStartTime?: number;
  browserSecureContext: ISecureContextMetadata;
};

// This must be here because we need the AccessProvider in the tree to evaluate
// the feature flag.
function EnableDualObservabilityProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const enableDualReporting = useFlag('enable_sus_dual_reporting');
  const {project} = useObservabilityConfig()!;

  return (
    <ObservabilityProvider
      _tmp_dualReporting={enableDualReporting}
      project={project}
    >
      {children}
    </ObservabilityProvider>
  );
}

export const ConnectElementWrapper: React.FC<Props> = ({
  initialRenderTime,
  connectJsLoadStartTime,
  fetchOverride,
  queryClient,
  children,
  deferredAuthPromise,
  submerchantAuthOverride,
  authInfoPropertiesOverride,
  publishableKey,
  connectElement,
  connectElementAttributes,
  connectElementSupplementalFunctions,
  connectElementSupplementalObjects,
  skipConnectElementEnabledCheck,
  eventEmitter,
  renderedState,
  frameMessenger,
  onboardingState,
  loggedOutState,
  platformViewportState,
  metaOptions,
  connectJsOptions,
  platformFrameFunctions,
  updateConnectJSOptions,
  isInternalDomain,
  requestIframe,
  browserSecureContext,
}) => {
  const intl = useIntl();
  const ref = React.useRef<HTMLElement | null>(null);
  const [inheritedStyles, setInheritedStyles] = React.useState<InheritedStyles>(
    {},
  );

  const {
    componentControlledBackground,
    enabledKey,
    ignoreThemingValues,
    messageOverrides,
    releaseState,
    requiresOnboarding,
    requiresAuthentication,
    disableMinWidth,
    maxWidth,
  } = CONNECT_ELEMENT_IMPORTS[connectElement];

  const boundaries: Intent = css({
    ...(typeof maxWidth === 'number' && {
      maxWidth: `${maxWidth}px`, // Maximum width of connect elements
    }),
    width: 'fill',
    minWidth: disableMinWidth ? `${0}px` : `${MIN_WIDTH}px`, // Minimum width of connect elements
    // Connect elements don't have a max width - we let platforms manage how it grows
    // (e.g. by setting a max width, adding margins, putting the connect element within a container, etc)
  });

  // This event is emitted here to give a better signal of when the component is about to paint to the DOM,
  // as opposed to when the component is starting its initial render.
  const loaded = React.useRef(false);
  React.useLayoutEffect(() => {
    // Prevents the event from being emitted multiple times
    if (!loaded.current) {
      eventEmitter.emitEvent('loaderstart', {});
      loaded.current = true;
    }
  }, [eventEmitter]);

  React.useEffect(() => {
    if (ref.current !== null) {
      setInheritedStyles({
        fontFamily: getComputedStyle(ref.current)?.fontFamily,
      });
    }
  }, []);

  return (
    <>
      <RenderedStateProvider renderedState={renderedState}>
        {/* We set this context as early as possible in the tree so that we can use it liberally */}
        <InternalDomainContext.Provider value={isInternalDomain}>
          <SailClassicThemeProvider
            ref={ref}
            style={{
              // Reset all inherited shadow DOM styles except font-family, which we explicitly inherit by default. Examples: font-variant, text-transform, word-spacing...
              all: 'initial',
              fontFamily: 'inherit', // Removing this breaks the "inherit" chain...
              ...(typeof maxWidth === 'number' && {
                display: 'flex',
                justifyContent: 'center',
              }),
            }}
          >
            <AuthProvider
              restrictToPlatformOwnsOnboarding={
                !!connectJsOptions.getPlatformSpecifiedValues().metaOptions
                  ?.restrictToPlatformOwnsOnboarding
              }
              publishableKey={publishableKey}
              authInfoPropertiesOverride={authInfoPropertiesOverride}
              deferredAuthPromise={deferredAuthPromise}
              isV2SessionFromMetaOptions={metaOptions?.isV2Session ?? false}
              isMobileSdk={
                !!connectJsOptions.getPlatformSpecifiedValues().metaOptions
                  ?.mobileSdk
              }
              eventEmitter={eventEmitter}
            >
              {(hasAuthError) => {
                const fallbackUI = (
                  <ConnectElementErrorBoundaryUi messages={messageOverrides} />
                );

                return (
                  <ConnectJsOptionsProvider
                    connectJsOptions={connectJsOptions}
                    inheritedStyles={inheritedStyles}
                    updateConnectJSOptions={updateConnectJSOptions}
                  >
                    {/* TODO: this logic should be integrated inside      */}
                    {/* ConnectSdkProvider, but it requires reading from  */}
                    {/* multiple contexts that are below it in the tree   */}
                    {/* (ConnectJsOptions and Auth).                      */}
                    <ConnectSdkProviderObservability>
                      <ConnectSdkProviderAccess>
                        <EnableDualObservabilityProvider>
                          <BorderBoxContainer styles={[boundaries]}>
                            <PlatformViewportProvider
                              platformViewportObserver={platformViewportState}
                            >
                              <ThemeProvider
                                component={connectElement}
                                componentControlledBackground={
                                  componentControlledBackground
                                }
                                ignoreThemingValues={
                                  ignoreThemingValues ?? false
                                }
                                requestIframe={requestIframe}
                              >
                                <MobileUIOverrides
                                  component={connectElement}
                                  isMobileSdk={
                                    !!connectJsOptions.getPlatformSpecifiedValues()
                                      .metaOptions?.mobileSdk
                                  }
                                >
                                  <ConnectElementErrorBoundary
                                    uuid="7GT7QB00"
                                    fallbackUI={fallbackUI}
                                    hasAuthError={hasAuthError}
                                  >
                                    <EventEmitterContext.Provider
                                      value={eventEmitter.emitEvent}
                                    >
                                      <ComponentLoadMetricContextProvider
                                        initialRenderTime={initialRenderTime}
                                        connectJsLoadStartTime={
                                          connectJsLoadStartTime
                                        }
                                      >
                                        <QueryClientProvider
                                          client={queryClient}
                                        >
                                          <AutoAnalyticsProvider>
                                            <SubmerchantAuthenticationContextProvider
                                              value={{
                                                locale: intl.locale,
                                                frameMessenger,
                                                submerchantAuthOverride,
                                                apiKeyOverride:
                                                  metaOptions?.apiKeyOverride,
                                                hostApp:
                                                  metaOptions?.hostApp ||
                                                  defaultHostApp,
                                              }}
                                            >
                                              <AttributeProvider
                                                component={connectElement}
                                                attributes={
                                                  connectElementAttributes
                                                }
                                              >
                                                <SupplementalFunctionProvider
                                                  component={connectElement}
                                                  supplementalFunctions={
                                                    connectElementSupplementalFunctions
                                                  }
                                                >
                                                  <SupplementalObjectProvider
                                                    component={connectElement}
                                                    supplementalObjects={
                                                      connectElementSupplementalObjects
                                                    }
                                                  >
                                                    <OnboardingProvider
                                                      onboardingStateObserver={
                                                        onboardingState
                                                      }
                                                      connectElement={
                                                        connectElement
                                                      }
                                                    >
                                                      <LoggedOutProvider
                                                        loggedOutStateObserver={
                                                          loggedOutState
                                                        }
                                                        connectedElement={
                                                          connectElement
                                                        }
                                                      >
                                                        <PlatformFrameContext.Provider
                                                          value={
                                                            platformFrameFunctions
                                                          }
                                                        >
                                                          <ConnectEmbeddedLibProvider
                                                            isInternalDomain={
                                                              isInternalDomain
                                                            }
                                                            customFonts={
                                                              connectJsOptions.getValues()
                                                                .fonts
                                                            }
                                                            fetchOverride={
                                                              fetchOverride
                                                            }
                                                          >
                                                            <KeyProviderWrapper>
                                                              <SecureContextEnforcer
                                                                connectElement={
                                                                  connectElement
                                                                }
                                                                browserSecureContext={
                                                                  browserSecureContext
                                                                }
                                                              >
                                                                <MobileSdkPreviewGateEnforcer
                                                                  connectElement={
                                                                    connectElement
                                                                  }
                                                                  metaOptions={
                                                                    connectJsOptions.getValues()
                                                                      .metaOptions
                                                                  }
                                                                >
                                                                  <AccountOnboardingContainer
                                                                    requiresOnboarding={
                                                                      requiresOnboarding
                                                                    }
                                                                    connectElement={
                                                                      connectElement
                                                                    }
                                                                  >
                                                                    <ConnectElementEnabledCheck
                                                                      connectElement={
                                                                        connectElement
                                                                      }
                                                                      enabledKey={
                                                                        enabledKey
                                                                      }
                                                                      releaseState={
                                                                        releaseState
                                                                      }
                                                                      skipEnabledCheck={
                                                                        !!skipConnectElementEnabledCheck
                                                                      }
                                                                      hostApp={
                                                                        connectJsOptions.getValues()
                                                                          .metaOptions
                                                                          .hostApp ??
                                                                        defaultHostApp
                                                                      }
                                                                    >
                                                                      <FeaturesProvider
                                                                        component={
                                                                          enabledKey
                                                                        }
                                                                      >
                                                                        <ExternalFlagOverrideWrapper>
                                                                          <SubmerchantInvalidSessionCheck
                                                                            requiresAuthentication={
                                                                              !!requiresAuthentication
                                                                            }
                                                                          >
                                                                            {
                                                                              children
                                                                            }
                                                                          </SubmerchantInvalidSessionCheck>
                                                                        </ExternalFlagOverrideWrapper>
                                                                      </FeaturesProvider>
                                                                    </ConnectElementEnabledCheck>
                                                                  </AccountOnboardingContainer>
                                                                </MobileSdkPreviewGateEnforcer>
                                                              </SecureContextEnforcer>
                                                            </KeyProviderWrapper>
                                                          </ConnectEmbeddedLibProvider>
                                                        </PlatformFrameContext.Provider>
                                                      </LoggedOutProvider>
                                                    </OnboardingProvider>
                                                  </SupplementalObjectProvider>
                                                </SupplementalFunctionProvider>
                                              </AttributeProvider>
                                            </SubmerchantAuthenticationContextProvider>
                                          </AutoAnalyticsProvider>
                                        </QueryClientProvider>
                                      </ComponentLoadMetricContextProvider>
                                    </EventEmitterContext.Provider>
                                  </ConnectElementErrorBoundary>
                                </MobileUIOverrides>
                              </ThemeProvider>
                            </PlatformViewportProvider>
                          </BorderBoxContainer>
                        </EnableDualObservabilityProvider>
                      </ConnectSdkProviderAccess>
                    </ConnectSdkProviderObservability>
                  </ConnectJsOptionsProvider>
                );
              }}
            </AuthProvider>
          </SailClassicThemeProvider>
        </InternalDomainContext.Provider>
      </RenderedStateProvider>
    </>
  );
};
